//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import classNames from 'classnames';
import React from 'react';

import { RCIconProps, RCIcon } from '../RCIcon/RCIcon';

// Matches RCButton: optional props use destructuring defaults (not defaultProps),
// and `...rest` forwards HTML attributes to the underlying element.
/* eslint-disable react/require-default-props, react/jsx-props-no-spreading */

export type UtilityColor = 'info' | 'loud' | 'neutral' | 'notice' | 'success' | 'urgent';
type UtilityFill = 'ultralight' | 'medium' | 'bold';

/**
 * A utility theme variant. The bare family (e.g. `info`) is the transparent,
 * accent-only form (no fill — pair with a `border`); a fill suffix adds a
 * background tint (`info-ultralight` / `info-medium` / `info-bold`).
 */
export type UtilityVariant = UtilityColor | `${UtilityColor}-${UtilityFill}`;

export type SurfaceVariant = 'primary' | 'on-primary';

/** A curated, brand-approved tempo combination. `{background}[-{accent-text-family}]`. */
export type TempoTheme =
  // High
  | 'vu-high'
  | 'tweed-high' | 'tweed-high-vu'
  | 'teal-high' | 'teal-high-kick'
  | 'kick-high' | 'kick-high-teal'
  | 'yellow-high' | 'yellow-high-vu'
  // Mid
  | 'vu-mid' | 'vu-mid-vu' | 'vu-mid-dark'
  | 'tweed-mid' | 'tweed-mid-vu'
  | 'teal-mid' | 'teal-mid-kick' | 'teal-mid-teal'
  | 'teal-mid-dark'
  | 'kick-mid-dark'
  // Neutral / low
  | 'neutral-vu' | 'neutral-kick' | 'neutral-teal';

type ThemeType = 'utility' | 'surface' | 'tempo';

// The theme is a top-level discriminated union: `themeType` selects the scheme
// and narrows the valid `variant`. Omit both for a plain, unthemed box.
type ThemeProps =
  | { themeType?: undefined; variant?: undefined }
  | { themeType: 'utility'; variant: UtilityVariant }
  | { themeType: 'surface'; variant?: SurfaceVariant }
  | { themeType: 'tempo'; variant: TempoTheme };

export type RCThemedBoxProps = React.ComponentPropsWithoutRef<'div'> & {
  /** Border style. `thin` is a full hairline border; `leading-accent` is a thick leading bar. Defaults to `none`. */
  border?: 'none' | 'thin' | 'leading-accent';
} & ThemeProps;

// The box owns only its decoration (theme colors, border). All sizing — padding,
// display, margin, border-radius, overflow — is left to utility classes via
// `className` (including their responsive variants), since this repo's utilities
// can't override what a component sets (see the cadence cascade convention).
const RCThemedBoxComponent = React.forwardRef<HTMLDivElement, RCThemedBoxProps>(
  function RCThemedBox({
    themeType = undefined,
    variant = undefined,
    border = 'none',
    className,
    children,
    ...rest
  }, ref) {
    return (
      <div
        ref={ref}
        className={classNames(
          'rc-themed-box',
          getThemeModifier(themeType, variant),
          { [`rc-themed-box--border-${border}`]: border !== 'none' },
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

interface RCThemedBoxIconProps extends RCIconProps {
  /** Controls whether the icon should be themed to match its parent box. Defaults to `true`. */
  themed?: boolean;
  /** Class(es) for the icon wrapper — e.g. a `text-color-accent-*` utility to recolor a non-themed icon. */
  className?: string;
}

function RCThemedBoxIcon({
  themed = true,
  icon: iconProp,
  className,
  ...iconPassthroughProps
}: RCThemedBoxIconProps) {
  return (
    <div className={classNames(
      'rc-themed-box__icon',
      { 'rc-themed-box__icon--themed': themed },
      className,
    )}>
      <RCIcon icon={iconProp} {...iconPassthroughProps} />
    </div>
  );
}

export const RCThemedBox = Object.assign(RCThemedBoxComponent, { Icon: RCThemedBoxIcon });

// Maps the theme to its modifier class, e.g. `rc-themed-box--utility-info-bold`.
// `surface` defaults its variant to `primary`; `utility`/`tempo` require one.
function getThemeModifier(themeType: ThemeType | undefined, variant: string | undefined): string | undefined {
  if (!themeType) return undefined;

  const resolved = themeType === 'surface' ? variant ?? 'primary' : variant;
  return resolved ? `rc-themed-box--${themeType}-${resolved}` : undefined;
}
